import React, { useEffect, useState } from 'react';
import { BTN, EYEBROW, chipClass } from '../liStyles';

// What the park's own staff say about a place — send guests here, don't send, feature
// it, hide it, or leave a note for whoever is on shift. Every change is saved at once
// and the place's staff block comes back from the database.
// Item 1 / PART A — leading icon for the NOTE label above the note input,
// matching the AskConsole ASK label pattern (leading icon + uppercase label).
// Inline <svg> mirroring the lucide-react "pencil" shape (the codebase
// convention; lucide-react is not a dependency).
const NoteIcon = () => (
  <svg className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);
// Item 1 (work-page-fixes) — leading icon for the FOR STAFF section label,
// matching the PARTY / ACCESS / WHEN label pattern in AskConsolePanel.js /
// PartyChips.js / TimeChips.js: an inline <svg> at w-3.5 h-3.5 mirroring the
// lucide-react "users" shape (the codebase convention; lucide-react is not a
// dependency — the same shape the PARTY group header uses in PartyChips.js).
// stroke="currentColor" lets it inherit the EYEBROW label color so it stays as
// subtle as the label text.
const UsersIcon = () => (
  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const StaffControls = ({ staff, onAction }) => {
  const s = staff || {};
  const [note, setNote] = useState(s.owner_note || '');
  const [busy, setBusy] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { setNote(s.owner_note || ''); }, [s.owner_note]);

  const run = async (action, value = null) => {
    setBusy(action);
    setError(null);
    try {
      await onAction(action, value);
      if (action === 'hide') setHidden(true);
      if (action === 'unhide') setHidden(false);
    } catch (e) {
      setError(e?.message || 'Could not save that.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="space-y-3" aria-label="Staff controls">
      <h3 className={`${EYEBROW} inline-flex items-center gap-1.5`}>
        <UsersIcon />
        For staff
      </h3>
      <div className="flex flex-wrap gap-1.5">
        <button type="button" disabled={Boolean(busy)} aria-pressed={s.send_guests_here === true} className={chipClass(s.send_guests_here === true)} onClick={() => run('send')}>
          Send guests here
        </button>
        <button type="button" disabled={Boolean(busy)} aria-pressed={s.do_not_send === true} className={chipClass(s.do_not_send === true)} onClick={() => run('dont_send')}>
          Don&rsquo;t send guests
        </button>
        {(s.send_guests_here != null || s.do_not_send) && (
          <button type="button" disabled={Boolean(busy)} className={chipClass(false)} onClick={() => run('clear')}>Clear</button>
        )}
        <button type="button" disabled={Boolean(busy)} aria-pressed={s.featured === true} className={chipClass(s.featured === true)} onClick={() => run(s.featured ? 'unfeature' : 'feature')}>
          {s.featured ? 'Featured' : 'Feature it'}
        </button>
        <button type="button" disabled={Boolean(busy)} className={chipClass(hidden)} onClick={() => run(hidden ? 'unhide' : 'hide')}>
          {hidden ? 'Hidden — show again' : 'Hide from every list'}
        </button>
      </div>
      {/* Item 1 / PART A — NOTE label above the note input, matching the ASK
          label pattern in AskConsole.js (leading icon + uppercase text-xs
          font-semibold tracking-wide gray label). Wrapped in space-y-1 so the
          label-to-input gap matches the ASK label-to-input gap. The placeholder
          stays but is no longer the only indicator of what the field is for. */}
      <div className="space-y-1">
        <div className="flex items-center gap-1.5">
          <NoteIcon />
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Note</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note for the desk — “ask for the patio”"
            aria-label="Staff note"
            className="flex-1 min-w-0 h-9 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400"
          />
          <button type="button" className={BTN} disabled={Boolean(busy) || note === (s.owner_note || '')} onClick={() => run('note', note.trim())}>
            {busy === 'note' ? 'Saving…' : 'Save note'}
          </button>
        </div>
      </div>
      {error && <p className="text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>}
    </section>
  );
};

export default StaffControls;
